import { isEqual } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'

import { calcReportParamsSection, resolveReportParams } from '@aytm/app/stats/page/question-views/ad/lib/report-params'

// Owns the ad-testing block state (the metric-selection config), seeded from the ad<key>
// report-param section and re-seeded whenever that section changes outside the block.
//
// The page view never reads the section itself: it receives an api on mount and pulls the
// report-param section from it when a change is announced through onStateChange. Nothing
// outside the block reads the config, so it is not mirrored onto the model.
export default function useAdTestingState({ params, onReady, onStateChange }) {
  const [state, setState] = useState(() => resolveReportParams({ section: params?.ad }))

  const snapshot = useRef()
  snapshot.current = state

  const api = useMemo(
    () => ({
      getReportParamsSection: () => calcReportParamsSection(snapshot.current),
      getExportData: () => ({ config: snapshot.current.config })
    }),
    []
  )

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  const sectionKey = JSON.stringify(params?.ad ?? null)
  const lastSectionKey = useRef(sectionKey)
  useEffect(() => {
    if (lastSectionKey.current === sectionKey) return
    lastSectionKey.current = sectionKey

    setState(resolveReportParams({ section: JSON.parse(sectionKey) }))
  }, [sectionKey])

  // The reason tells the view what the change means: 'seed' is the resolved starting state,
  // 'state' a change the researcher made.
  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  const lastState = useRef(null)
  useEffect(() => {
    const reason = null === lastState.current ? 'seed' : 'state'
    lastState.current = state

    onStateChangeRef.current?.(reason)
  }, [state])

  // The config is rebuilt on every pick, so a subset equal to the active one arrives as a fresh
  // object -- compared by value, not identity, or re-picking what is already selected would write.
  const onModelChange = useMemo(
    () =>
      (change = {}) =>
        setState(current =>
          Object.entries(change).every(([key, value]) => isEqual(current[key], value))
            ? current
            : { ...current, ...change }
        ),
    []
  )

  return { ...state, onModelChange }
}
